/**
 * demoRaceContent.ts (M112) — the PURE half of the onboarding DEMO RACE.
 *
 * The owner's spec (2026-07-31): after claiming a Racer ID the player drops
 * into a live solo race that FREEZES under each teaching pop-up and resumes
 * the moment they perform the taught action, in this order:
 *
 *   RULE  — "FIRST BALL INTO THE GOAL WINS" (tap/key to kick off; M157:
 *           the finish line became a goal — owner-approved wording)
 *   DRIVE — device-true driving controls (dismissed the moment they drive)
 *   BALL  — push/handle the ball (dismissed on the first real touch)
 *   ITEM  — collect a ? box, then FIRE the boost; when the boost's run ends,
 *           the demo smoothly hands off to the unlock video.
 *
 * M115 (owner): the FIRE step (3 of 3) is the ONE lesson that never freezes.
 * The player keeps driving with the module up, the item button's coach cue
 * ESCALATES the longer they take (demoCoachLevel), and after the fire the
 * 'fired' step runs until the boost's own active window ends — so they get to
 * drive the speed they just triggered before the transition out.
 *
 * This module owns the step MACHINE (a pure reducer the director ticks with
 * per-frame observations) and every line of COPY, per device (touch phones,
 * keyboards, gamepads) — so the sequencing and the words are unit-testable
 * without a WebGL Game. The DemoRaceDirector owns the DOM/game side.
 *
 * Copy rules: UPPERCASE, no em dashes (the M86b owner rule), middots join
 * clauses. The RULE headline is the owner's exact sentence.
 */

export type DemoDevice = 'touch' | 'keys' | 'pad';

/** Pick the popup voice for this session. A connected pad only wins when the
 *  device isn't touch-first (a phone with a paired pad still teaches touch —
 *  the joystick is what's on screen). */
export function detectDemoDevice(coarsePointer: boolean, hasGamepad: boolean): DemoDevice {
  if (coarsePointer) return 'touch';
  return hasGamepad ? 'pad' : 'keys';
}

export type DemoStepId =
  | 'rule' // frozen intro card; any tap/key releases
  | 'drive' // frozen until driving input; a short driven beat completes it
  | 'ball' // frozen until driving; completes on the first real ball touch
  | 'carry' // no popup: carry the ball a stretch so the dribble sinks in
  | 'item' // frozen until driving; completes when a collected item SETTLES
  | 'use' // M115: LIVE (never frozen); completes when the boost engages
  | 'fired' // no popup: the boost is running; completes when it ends
  | 'done'; // hand off to the unlock video

/** What the director reads off the live sim every frame. */
export interface DemoObs {
  /** Player car speed (u/s). */
  carSpeed: number;
  /** Car ↔ own-ball distance (u). */
  ballDist: number;
  /** Own ball speed (u/s). */
  ballSpeed: number;
  /** Own ball course arc (u down-course). */
  ballArc: number;
  /** The slot settled with a fireable item (held, roulette done). */
  itemSettled: boolean;
  /** An item effect is currently running. */
  itemActive: boolean;
  /** The player crossed the finish (safety: ends the demo early). */
  finished: boolean;
}

export interface DemoState {
  step: DemoStepId;
  /** The frozen popup's action arrived — the sim may run. Meaningless on the
   *  never-frozen steps (carry/use/fired/done). */
  released: boolean;
  /** Seconds of real driving accumulated during the DRIVE step. */
  driveT: number;
  /** The ball arc that completes the CARRY stretch (set at the first touch). */
  goalArc: number | null;
  /** M115: seconds spent on the live FIRE step — drives the escalating cue. */
  useT: number;
}

/** Feel knobs (exported for the director + tests). */
export const DEMO_DRIVE_BEAT_S = 1.6; // driving felt before the ball lesson
export const DEMO_DRIVE_MIN_SPEED = 8; // "actually moving" for that beat
/** Owner 2026-09-16: the dribble stretch between the PUSH YOUR BALL lesson
 *  and the GRAB A ? BOX freeze was ~1 s (30 u); it is a real run now. */
export const DEMO_CARRY_DIST = 120; // ball carried this far before the item lesson
/** Owner 2026-09-16: the demo drops NYC's first ? box row (arc 140 — it
 *  would sit INSIDE the longer carry) so the first boxes are the second row
 *  (~arc 290-340): a clear lead-in after the GRAB A ? BOX popup releases.
 *  Flow.runDemoRace applies it via Race.trimPickupsBefore. */
export const DEMO_FIRST_BOX_MIN_ARC = 260;
export const DEMO_BALL_TOUCH_DIST = 4.6; // car↔ball contact distance (box 1.9 + ball 1.6 + slack)
export const DEMO_BALL_TOUCH_SPEED = 3.5; // the touch must MOVE the ball (no grid creep)
/** "The player started driving" gate on the merged InputFrame — well past the
 *  joystick deadzone (~4 px) so a resting thumb can't release a popup. */
export const DEMO_RELEASE_STEER = 0.28;
export const DEMO_RELEASE_THROTTLE = 0.15;
/** M115: seconds on the live FIRE step before the boost-button cue escalates
 *  from "loud" to "unmissable" (demoCoachLevel 1 → 2). */
export const DEMO_USE_URGENT_S = 3.5;

export function initialDemoState(): DemoState {
  return { step: 'rule', released: false, driveT: 0, goalArc: null, useT: 0 };
}

/** True while the sim must stay frozen (a popup is waiting on its action).
 *  M115 (owner rule): the FIRE lesson ('use') is LIVE — the player keeps
 *  driving with the module up, so it joins carry/fired/done here. */
export function demoFrozen(s: DemoState): boolean {
  if (s.step === 'carry' || s.step === 'use' || s.step === 'fired' || s.step === 'done') {
    return false;
  }
  return !s.released;
}

/** M115: how loud the item-button coach cue should be right now.
 *  0 = off, 1 = the standing FIRE cue, 2 = escalated (they're taking a while). */
export function demoCoachLevel(s: DemoState): 0 | 1 | 2 {
  if (s.step !== 'use') return 0;
  return s.useT >= DEMO_USE_URGENT_S ? 2 : 1;
}

/** The popup to show for this state (null = clear track, no popup). The DRIVE
 *  popup dismisses the moment its action arrives (owner rule); BALL/ITEM/USE
 *  persist past the release until their objective completes. */
export function demoPopupStep(s: DemoState): DemoStepId | null {
  if (s.step === 'carry' || s.step === 'fired' || s.step === 'done') return null;
  if (s.step === 'drive' && s.released) return null;
  return s.step;
}

/** The frozen popup's action arrived (rule tap / driving input / fire press). */
export function releaseDemoStep(s: DemoState): DemoState {
  if (s.step === 'rule') {
    // The kick-off tap advances straight into the driving lesson (frozen).
    return { ...s, step: 'drive', released: false };
  }
  return s.released ? s : { ...s, released: true };
}

/** One observation tick. Pure — the director diffs `step` for praise/sfx. */
export function tickDemo(s: DemoState, obs: DemoObs, dt: number): DemoState {
  // Safety valve: however we got here, a finished player ends the demo.
  if (obs.finished && s.step !== 'done') return { ...s, step: 'done' };
  switch (s.step) {
    case 'drive': {
      if (!s.released) return s;
      const driving = obs.carSpeed > DEMO_DRIVE_MIN_SPEED;
      const driveT = driving ? s.driveT + dt : s.driveT;
      if (driveT >= DEMO_DRIVE_BEAT_S) return { ...s, driveT, step: 'ball', released: false };
      return driveT === s.driveT ? s : { ...s, driveT };
    }
    case 'ball': {
      if (!s.released) return s;
      const touched = obs.ballDist <= DEMO_BALL_TOUCH_DIST && obs.ballSpeed >= DEMO_BALL_TOUCH_SPEED;
      if (touched) return { ...s, step: 'carry', goalArc: obs.ballArc + DEMO_CARRY_DIST };
      return s;
    }
    case 'carry': {
      if (s.goalArc !== null && obs.ballArc >= s.goalArc) {
        return { ...s, step: 'item', released: false };
      }
      return s;
    }
    case 'item': {
      // A box grabbed during the carry stretch settles here instantly. The
      // fire lesson opens ALREADY released (M115: it never freezes).
      if (obs.itemSettled) return { ...s, step: 'use', released: true, useT: 0 };
      return s;
    }
    case 'use': {
      // Live: the car keeps driving under the module. The fire itself is the
      // only exit, and the clock feeds the escalating cue (demoCoachLevel).
      if (obs.itemActive) return { ...s, step: 'fired' };
      return { ...s, useT: s.useT + dt };
    }
    case 'fired': {
      if (!obs.itemActive) return { ...s, step: 'done' };
      return s;
    }
    default:
      return s; // 'rule' waits on its explicit release; 'done' is terminal
  }
}

// ---- Copy ------------------------------------------------------------------

export interface DemoPopup {
  kicker: string;
  headline: string;
  sub: string;
  /** The pulsing bottom line naming the release action ('' = the sub IS it). */
  action: string;
  /** Small visual aid the overlay renders beside the copy. */
  mock: 'joystick' | 'arrows' | 'stick' | 'itembtn' | 'space' | 'abtn' | null;
}

const RULE_ACTION: Record<DemoDevice, string> = {
  touch: 'TAP ANYWHERE TO KICK OFF',
  keys: 'PRESS ANY KEY TO KICK OFF',
  pad: 'PRESS ANY BUTTON TO KICK OFF',
};
const DRIVE_SUB: Record<DemoDevice, string> = {
  touch: 'DRAG THE JOYSTICK TO DRIVE · PULL IT BACK TO BRAKE AND REVERSE',
  keys: 'DRIVE WITH THE ARROW KEYS OR WASD',
  pad: 'STEER WITH THE LEFT STICK · RT DRIVES · LT REVERSES',
};
const DRIVE_MOCK: Record<DemoDevice, DemoPopup['mock']> = {
  touch: 'joystick',
  keys: 'arrows',
  pad: 'stick',
};
const USE_SUB: Record<DemoDevice, string> = {
  touch: 'TAP THE BIG ITEM BUTTON TO FIRE',
  keys: 'PRESS SPACE TO FIRE',
  pad: 'PRESS A TO FIRE',
};
/** M115: the live FIRE step's pulsing bottom line. It says KEEP DRIVING on
 *  purpose (the sim no longer freezes) and escalates if they take a while. */
const USE_ACTION: Record<DemoDevice, string> = {
  touch: 'KEEP DRIVING · TAP IT WHEN YOU ARE READY',
  keys: 'KEEP DRIVING · HIT IT WHEN YOU ARE READY',
  pad: 'KEEP DRIVING · HIT IT WHEN YOU ARE READY',
};
const USE_ACTION_URGENT: Record<DemoDevice, string> = {
  touch: 'TAP THE GLOWING BUTTON NOW!',
  keys: 'HIT SPACE NOW!',
  pad: 'HIT A NOW!',
};
const USE_MOCK: Record<DemoDevice, DemoPopup['mock']> = {
  touch: 'itembtn',
  keys: 'space',
  pad: 'abtn',
};

/** The teaching popups. Steps without one (carry/fired/done) return null.
 *  `urgent` (M115) only changes the live FIRE step's action line. */
export function demoPopup(step: DemoStepId, device: DemoDevice, urgent = false): DemoPopup | null {
  switch (step) {
    case 'rule':
      return {
        kicker: 'THE ONE RULE',
        headline: 'FIRST BALL INTO THE GOAL WINS',
        sub: 'YOUR BALL WINS THE RACE, NOT YOUR CAR',
        action: RULE_ACTION[device],
        mock: null,
      };
    case 'drive':
      return {
        kicker: 'DRIVING SCHOOL · 1 OF 3',
        headline: 'TAKE THE WHEEL',
        sub: DRIVE_SUB[device],
        action: 'GO ON, FLOOR IT',
        mock: DRIVE_MOCK[device],
      };
    case 'ball':
      return {
        kicker: 'DRIVING SCHOOL · 2 OF 3',
        headline: 'PUSH YOUR BALL',
        sub: 'DRIVE INTO YOUR BALL AND CARRY IT DOWN THE STREET',
        action: "DRIVE ON WHEN YOU'RE READY",
        mock: null,
      };
    case 'item':
      return {
        kicker: 'DRIVING SCHOOL · 3 OF 3',
        headline: 'GRAB A ? BOX',
        sub: 'DRIVE THROUGH A GLOWING ? BOX TO COLLECT AN ITEM',
        action: "DRIVE ON WHEN YOU'RE READY",
        mock: null,
      };
    case 'use':
      return {
        kicker: 'DRIVING SCHOOL · 3 OF 3',
        headline: 'FIRE YOUR BOOST',
        sub: USE_SUB[device],
        action: urgent ? USE_ACTION_URGENT[device] : USE_ACTION[device],
        mock: USE_MOCK[device],
      };
    default:
      return null;
  }
}

/** Gold praise pops on objective completion (keyed by the step TRANSITION). */
export function demoPraise(from: DemoStepId, to: DemoStepId): string | null {
  if (from === 'drive' && to === 'ball') return 'NICE DRIVING!';
  if (from === 'ball' && to === 'carry') return "THAT'S IT! KEEP IT ROLLING!";
  if (from === 'item' && to === 'use') return 'ITEM ABOARD!';
  if (from === 'fired' && to === 'done') return "BOOM! YOU'RE READY TO RACE!";
  return null;
}

// ---- The unlock video (the 3-shot animation after the demo) ----------------

export interface UnlockShotMedia {
  kind: 'city' | 'ride' | 'style';
  id: string;
}

export interface UnlockShot {
  kicker: string;
  headline: string;
  sub: string;
  media: UnlockShotMedia[];
}

/** Seconds per shot (the whole video ≈ 10 s; a tap advances early). */
export const UNLOCK_SHOT_S = 3.4;

/** The owner's three shots, verbatim phrases split across headline + sub. */
export const UNLOCK_SHOTS: UnlockShot[] = [
  {
    kicker: 'THE WORLD TOUR',
    headline: 'UNLOCK NEW COURSES',
    sub: 'ON TOUR!',
    media: [
      { kind: 'city', id: 'london' },
      { kind: 'city', id: 'tokyo' },
      { kind: 'city', id: 'rio' },
    ],
  },
  {
    kicker: 'YOUR GARAGE',
    headline: 'UNLOCK NEW RIDES',
    sub: 'IN YOUR GARAGE BY GAINING XP!',
    media: [
      { kind: 'ride', id: 'sports' },
      { kind: 'ride', id: 'muscle' },
      { kind: 'ride', id: 'motorcycle' },
    ],
  },
  {
    kicker: 'THE SHOP',
    headline: 'SPEND YOUR SP',
    sub: 'ON NEW PAINT JOBS AND WHEELS IN THE SHOP!',
    media: [
      { kind: 'style', id: 'wrap.brazil' },
      { kind: 'style', id: 'paint.blaugrana' },
      { kind: 'style', id: 'tires.copa' },
    ],
  },
];
